//apps\web\src\views\public-views\qr-reader\index.js
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  Html5Qrcode,
} from 'html5-qrcode'

import {
  Button,
  Typography,
} from 'antd'

import {
  CameraOutlined,
  ReloadOutlined,
} from '@ant-design/icons'


const {
  Title,
  Text,
} = Typography


const QR_READER_ELEMENT_ID =
  'river-race-qr-reader'


const QrReaderPage = () => {
  const scannerRef =
    useRef(null)

  const mountedRef =
    useRef(true)

  const navigatingRef =
    useRef(false)


  const [status, setStatus] =
    useState(
      'Preparando câmera...',
    )

  const [error, setError] =
    useState('')

  const [cameraStarted, setCameraStarted] =
    useState(false)


  // ======================================================
  // PARAR CÂMERA
  // ======================================================

  const stopScanner =
    useCallback(
      async () => {
        const scanner =
          scannerRef.current

        if (!scanner) {
          return
        }

        try {
          if (scanner.isScanning) {
            await scanner.stop()
          }
        } catch (stopError) {
          console.warn(
            '[QR READER] Erro ao parar câmera:',
            stopError,
          )
        }

        try {
          scanner.clear()
        } catch (clearError) {
          // Ignorar.
        }

        scannerRef.current =
          null
      },
      [],
    )


  // ======================================================
  // VALIDAR QR
  // ======================================================

  const resolveJoinUrl =
    useCallback(
      (decodedText) => {
        if (!decodedText) {
          return null
        }

        const value =
          decodedText.trim()

        if (!value) {
          return null
        }


        // ----------------------------------------------
        // QR contendo somente:
        //
        // /join/TOKEN
        // ----------------------------------------------

        if (
          value.startsWith(
            '/join/',
          )
        ) {
          return value
        }


        // ----------------------------------------------
        // URL completa:
        //
        // https://games.nlbsolucoes.com/join/TOKEN
        // ----------------------------------------------

        try {
          const parsedUrl =
            new URL(value)

          if (
            !parsedUrl.pathname.startsWith(
              '/join/',
            )
          ) {
            return null
          }

          return (
            parsedUrl.pathname +
            parsedUrl.search +
            parsedUrl.hash
          )
        } catch (parseError) {
          return null
        }
      },
      [],
    )


  // ======================================================
  // QR LIDO
  // ======================================================

  const handleQrRead =
    useCallback(
      async (decodedText) => {
        if (
          navigatingRef.current
        ) {
          return
        }


        const joinUrl =
          resolveJoinUrl(
            decodedText,
          )


        if (!joinUrl) {
          setError(
            'Este QR Code não pertence ao cadastro do jogo.',
          )

          setStatus(
            'Aponte para o QR Code do jogo.',
          )

          return
        }


        navigatingRef.current =
          true


        setError('')

        setStatus(
          'QR Code identificado. Abrindo cadastro...',
        )


        await stopScanner()


        // ----------------------------------------------
        // MESMA ABA.
        //
        // Não cria nova janela.
        // Não acumula abas.
        // ----------------------------------------------

        const separator =
          joinUrl.includes('?')
            ? '&'
            : '?'

        const readerJoinUrl =
          `${joinUrl}${separator}reader=1`

        window.location.assign(
          readerJoinUrl,
        )
      },
      [
        resolveJoinUrl,
        stopScanner,
      ],
    )


  // ======================================================
  // INICIAR CÂMERA
  // ======================================================

  const startScanner =
    useCallback(
      async () => {
        if (
          scannerRef.current
        ) {
          return
        }


        navigatingRef.current =
          false

        setError('')

        setStatus(
          'Solicitando acesso à câmera...',
        )


        try {
          const scanner =
            new Html5Qrcode(
              QR_READER_ELEMENT_ID,
            )


          scannerRef.current =
            scanner


          await scanner.start(
            {
              facingMode:
                'environment',
            },

            {
              fps: 10,

              qrbox: (
                viewfinderWidth,
                viewfinderHeight,
              ) => {
                const minEdge =
                  Math.min(
                    viewfinderWidth,
                    viewfinderHeight,
                  )

                const size =
                  Math.floor(
                    minEdge *
                    0.72,
                  )


                return {
                  width: size,
                  height: size,
                }
              },
            },

            (decodedText) => {
              handleQrRead(
                decodedText,
              )
            },

            () => {
              // Falhas normais de leitura
              // são ignoradas.
            },
          )


          if (
            mountedRef.current
          ) {
            setCameraStarted(
              true,
            )

            setStatus(
              'Aponte para o QR Code do jogo.',
            )
          }
        } catch (cameraError) {
          console.error(
            '[QR READER] Erro:',
            cameraError,
          )


          scannerRef.current =
            null


          if (
            mountedRef.current
          ) {
            setCameraStarted(
              false,
            )

            setStatus(
              'Não foi possível iniciar a câmera.',
            )

            setError(
              'Verifique a permissão da câmera no navegador e tente novamente.',
            )
          }
        }
      },
      [
        handleQrRead,
      ],
    )


  // ======================================================
  // MONTAGEM
  // ======================================================

  useEffect(
    () => {
      mountedRef.current =
        true


      startScanner()


      return () => {
        mountedRef.current =
          false

        stopScanner()
      }
    },
    [
      startScanner,
      stopScanner,
    ],
  )


  // ======================================================
  // TENTAR NOVAMENTE
  // ======================================================

  const handleRetry =
    async () => {
      await stopScanner()

      setCameraStarted(
        false,
      )

      startScanner()
    }


  // ======================================================
  // RENDER
  // ======================================================

  return (
    <div
      style={{
        minHeight: '100vh',
        background:
          'linear-gradient(180deg, #07172b 0%, #0b3155 100%)',

        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        padding: 20,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 520,

          background:
            '#ffffff',

          borderRadius: 24,

          padding: 24,

          boxShadow:
            '0 20px 60px rgba(0, 0, 0, 0.30)',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            marginBottom: 22,
          }}
        >
          <CameraOutlined
            style={{
              fontSize: 42,
              marginBottom: 12,
            }}
          />

          <Title
            level={2}
            style={{
              margin: 0,
            }}
          >
            Leitor de QR Code
          </Title>

          <Text
            type="secondary"
          >
            River Race
          </Text>
        </div>


        <div
          style={{
            overflow: 'hidden',

            borderRadius: 18,

            background:
              '#000000',

            minHeight: 300,
          }}
        >
          <div
            id={
              QR_READER_ELEMENT_ID
            }
            style={{
              width: '100%',
            }}
          />
        </div>


        <div
          style={{
            textAlign: 'center',
            marginTop: 20,
          }}
        >
          <Text
            strong
            style={{
              fontSize: 16,
            }}
          >
            {status}
          </Text>


          {error && (
            <div
              style={{
                marginTop: 10,
              }}
            >
              <Text
                type="danger"
              >
                {error}
              </Text>
            </div>
          )}


          {!cameraStarted && (
            <Button
              type="primary"
              size="large"

              icon={
                <ReloadOutlined />
              }

              onClick={
                handleRetry
              }

              style={{
                marginTop: 20,
              }}
            >
              Tentar novamente
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}


export default QrReaderPage